import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInRouterContext, useLocation, useSearchParams } from 'react-router-dom';

import type { SortState } from '../components/DataTable';
import { queryInMemoryWindow, type InMemoryListConfig } from '../lib/listPage';

/**
 * The one list hook (DESIGN_SYSTEM.md §5 Lists). Every table and every picker
 * over an unbounded set loads through it, in batches, by scrolling.
 *
 * What it owns:
 *  - batches of LIST_BATCH_SIZE, continued by an opaque cursor the fetcher
 *    hands back; `hasMore` is `nextCursor !== null` and is never compared with
 *    a count, so a stale total can never end a list early
 *  - one request in flight per list, and a query generation per request: a
 *    response for anything but the current query is discarded, even when the
 *    fetcher ignores `signal`
 *  - two reset levels and no others (rulings 3, 4). A search, sort or filter
 *    change reloads from the top at 'refreshing' — rows stay mounted and dim,
 *    then are replaced. `resetKey` is the ORGANIZATION ID and nothing else:
 *    when it changes (not on first mount) the rows go, search/sort/filters
 *    return to their defaults, their URL params are removed, and status is
 *    'initial' (skeletons)
 *  - dedupe by row key; two batches in a row that add nothing → 'stalled'
 *  - at most LIST_AUTO_FILL_MAX loads without user input, and a cap of
 *    LIST_ROW_CAP mounted rows → 'capped'
 *  - the mutation rules (ruling 20): prependItem pins a created row first,
 *    patchItem replaces a row where it is, removeItem drops it. None of them
 *    fetches; marks ('new' / 'updated') clear on the next reset
 *  - `urlState`: q, sort/dir and each filter under its own key, written with
 *    replace, defaults omitted, never a scroll offset or an organization
 *
 * A `source: 'memory'` list (Roles, App Clients, Processor Templates) holds
 * every row already; it reveals LIST_BATCH_SIZE more at a time through the
 * same states with no network call.
 */

export const LIST_BATCH_SIZE = 50;
/** Mounted rows before auto-loading stops (map-accessibility imports this name). */
export const LIST_ROW_CAP = 1000;
/** Batches that may load without a scroll, wheel, touch or click in between. */
export const LIST_AUTO_FILL_MAX = 3;

/**
 * The organization each URL-state list last ran under, by route path. It
 * outlives a mount on purpose: an organization switch remounts the page, and
 * this is how the remounted list knows the URL's query belongs to the
 * organization it just left.
 */
const lastResetKeyByPath = new Map<string, { key: string; switchSeq: number }>();

/**
 * Incremented by the shell on every change of selected organization. A switch
 * can happen while a list is NOT mounted — its page withdrawn by PermissionGate
 * under an organization that lacks the grant — and the list can then come back
 * under the SAME organization it last ran in (A → B → A). Organization ids alone
 * would call that a return to the page and seed it from the ?q= / ?scope= left
 * in the URL before the switch; the count says a switch happened in between.
 */
let organizationSwitchSeq = 0;

/** Call on every change of selected organization, before the new one renders. */
export function noteOrganizationSwitch(): void {
  organizationSwitchSeq += 1;
}

export type ListStatus =
  | 'initial'
  | 'ready'
  | 'refreshing'
  | 'loadingMore'
  | 'appendError'
  | 'stalled'
  | 'capped'
  | 'error';

export type RowMark = 'new' | 'updated';

export type FilterValue = string | boolean;
export type FilterValues = Record<string, FilterValue>;
export type FilterConfig = Record<string, { default: FilterValue; allowed?: readonly string[] }>;

export interface ListSort<S extends string> {
  column: S;
  direction: 'asc' | 'desc';
}

export interface ListBatch<T> {
  items: T[];
  /** null ends the list. Only the server decides. */
  nextCursor: string | null;
  /** A number on the first batch; later batches may omit it. */
  totalCount?: number | null;
}

export interface ListBatchParams<S extends string = string> {
  cursor: string | null;
  limit: number;
  /** Trimmed, debounced search — '' when none. */
  search: string;
  sort?: ListSort<S>;
  filters: FilterValues;
  signal: AbortSignal;
}

export interface ListRemoval {
  /** Index the removed row had. */
  index: number;
  /** Key of the row that took its place (or the one before it), null when none remain. */
  nextKey: string | null;
  /** Increments per removal so a repeat of the same shape still reads as new. */
  token: number;
}

interface CommonOptions<T, S extends string> {
  rowKey: (row: T) => string;
  /** false holds the list at 'initial' (skeletons) until prerequisites exist. */
  enabled?: boolean;
  /** The organization id. Never a filter (ruling 3). */
  resetKey?: string | null;
  initialSort?: ListSort<S>;
  /** Allowlist: an unknown column from the UI or the URL is ignored. */
  sortColumns?: readonly S[];
  filters?: FilterConfig;
  /** True on every full-page list; never in dialogs or pickers (ruling 5). Needs a Router. */
  urlState?: boolean;
  debounceMs?: number;
  /** Label for the console.error of a failed load. */
  errorContext?: string;
  capAt?: number;
  /** Serialises this list's requests with another reader on the same page (Sensors' map). */
  queue?: ListRequestQueue;
}

export interface ServerListOptions<T, S extends string> extends CommonOptions<T, S> {
  source?: 'server';
  fetcher: (params: ListBatchParams<S>) => Promise<ListBatch<T>>;
  /** Adjusts a stored cursor after a served row is removed (offset − 1). */
  cursorAfterRemove?: (cursor: string) => string;
}

export interface MemoryListOptions<T, S extends string> extends CommonOptions<T, S> {
  source: 'memory';
  /** Every row. Runs on mount, on an organization switch, on reset() and when `sourceKey` changes. */
  load: (params: { signal: AbortSignal }) => Promise<readonly T[]> | readonly T[];
  memory: InMemoryListConfig<T, S> & { filter?: (row: T, filters: FilterValues) => boolean };
  /**
   * Identity of the data `load` reads. Changing it re-reads and re-queries
   * WITHOUT resetting the revealed depth — the data changed, the query did not.
   *
   * DANGER: compared with Object.is. Pass a stable value (the source array
   * itself); a literal rebuilt on every render is an infinite load loop.
   */
  sourceKey?: unknown;
}

export type UseInfiniteListOptions<T, S extends string = string> =
  | ServerListOptions<T, S>
  | MemoryListOptions<T, S>;

export interface InfiniteList<T, S extends string = string> {
  items: T[];
  totalCount: number | null;
  totalIsApproximate: boolean;
  hasMore: boolean;
  status: ListStatus;
  error: unknown;
  loadMore: (options?: { auto?: boolean }) => void;
  retryBatch: () => void;
  reset: () => void;
  patchItem: (key: string, fn: (row: T) => T, options?: { mark?: RowMark }) => void;
  removeItem: (key: string) => void;
  prependItem: (row: T, options?: { mark?: RowMark }) => void;
  searchInput: string;
  setSearchInput: (value: string) => void;
  clearSearch: () => void;
  sort: ListSort<S> | undefined;
  onSortChange: (sort: SortState) => void;
  filters: FilterValues;
  setFilter: (name: string, value: FilterValue) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  /** Named extension: the APPLIED (debounced, trimmed) search, for empty-state copy and dependent reads. */
  search: string;
  /** DataTable plumbing: row marks. */
  marks: ReadonlyMap<string, RowMark>;
  /** DataTable plumbing: increments each time a reset resolves (scrollTop 0, focus). */
  generation: number;
  /** DataTable plumbing: where focus goes after a removal. */
  lastRemoval: ListRemoval | null;
}

/* ------------------------------------------------------------------ */
/* Request queue                                                       */
/* ------------------------------------------------------------------ */

export interface ListRequestQueue {
  run<R>(task: () => Promise<R>): Promise<R>;
}

/**
 * One task at a time. Sensors shares one between its table and its map walk so
 * a page never holds two list requests against a database pool of two.
 */
export function createSerialQueue(): ListRequestQueue {
  let tail: Promise<unknown> = Promise.resolve();
  return {
    run<R>(task: () => Promise<R>): Promise<R> {
      const next = tail.then(task, task);
      tail = next.catch(() => undefined);
      return next;
    },
  };
}

/* ------------------------------------------------------------------ */
/* Internals                                                           */
/* ------------------------------------------------------------------ */

const isAbort = (err: unknown) =>
  (err instanceof DOMException && err.name === 'AbortError') ||
  (typeof err === 'object' && err !== null && (err as { name?: unknown }).name === 'AbortError');

const defaultsOf = (config: FilterConfig | undefined): FilterValues => {
  const out: FilterValues = {};
  for (const [name, entry] of Object.entries(config ?? {})) out[name] = entry.default;
  return out;
};

const acceptsFilter = (config: FilterConfig | undefined, name: string, value: FilterValue): boolean => {
  const entry = config?.[name];
  if (!entry) return false;
  if (typeof entry.default === 'boolean') return typeof value === 'boolean';
  if (typeof value !== 'string') return false;
  return !entry.allowed || entry.allowed.includes(value);
};

const sameFilters = (a: FilterValues, b: FilterValues) => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) if (a[key] !== b[key]) return false;
  return true;
};

interface UrlSeed<S extends string> {
  search: string;
  sort: ListSort<S> | undefined;
  filters: FilterValues;
}

function readUrlSeed<S extends string>(
  params: URLSearchParams | null,
  config: FilterConfig | undefined,
  sortColumns: readonly S[] | undefined,
  initialSort: ListSort<S> | undefined,
): UrlSeed<S> {
  const filters = defaultsOf(config);
  if (!params) return { search: '', sort: initialSort, filters };
  const search = (params.get('q') ?? '').trim();
  let sort = initialSort;
  const column = params.get('sort');
  if (column && (!sortColumns || (sortColumns as readonly string[]).includes(column))) {
    const dir = params.get('dir');
    sort = { column: column as S, direction: dir === 'desc' ? 'desc' : dir === 'asc' ? 'asc' : initialSort?.direction ?? 'asc' };
  }
  for (const [name, entry] of Object.entries(config ?? {})) {
    const raw = params.get(name);
    if (raw === null) continue;
    if (typeof entry.default === 'boolean') {
      if (raw === 'true' || raw === 'false') filters[name] = raw === 'true';
    } else if (!entry.allowed || entry.allowed.includes(raw)) {
      filters[name] = raw;
    }
  }
  return { search, sort, filters };
}

interface ServerState<T> {
  /** The resetKey these rows belong to. */
  scope: string | null;
  items: T[];
  nextCursor: string | null;
  totalCount: number | null;
  status: ListStatus;
  error: unknown;
  /**
   * A batch for this scope has loaded at least once. A filter, search or sort
   * change after that is 'refreshing' even when the last result was empty —
   * skeletons belong to the first load and an organization switch only.
   */
  loaded: boolean;
  marks: Map<string, RowMark>;
  generation: number;
  lastRemoval: ListRemoval | null;
}

/* ------------------------------------------------------------------ */
/* Hook                                                                */
/* ------------------------------------------------------------------ */

export function useInfiniteList<T, S extends string = string>(
  options: UseInfiniteListOptions<T, S>,
): InfiniteList<T, S> {
  const {
    rowKey,
    enabled = true,
    resetKey = null,
    initialSort,
    sortColumns,
    filters: filterConfig,
    urlState = false,
    debounceMs = 350,
    errorContext,
    capAt = LIST_ROW_CAP,
    queue,
  } = options;
  const isMemory = options.source === 'memory';

  // Latest callables in refs: callers need no useCallback.
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const rowKeyRef = useRef(rowKey);
  rowKeyRef.current = rowKey;

  // ---- URL state -----------------------------------------------------
  // `urlState` and the presence of a Router are fixed for the life of a
  // mount, so this branch never changes the order of hook calls.
  const urlStateRef = useRef(urlState);
  const inRouter = useInRouterContext();
  const urlEnabled = urlStateRef.current && inRouter;
  /* eslint-disable react-hooks/rules-of-hooks -- stable per mount, see above */
  const [searchParams, setSearchParams] = urlEnabled
    ? useSearchParams()
    : [null, null];
  const routePath = urlEnabled ? useLocation().pathname : '';
  /* eslint-enable react-hooks/rules-of-hooks */

  /*
   * An organization switch usually REMOUNTS the page rather than changing
   * resetKey under it: PermissionGate swaps the page for a skeleton while the
   * new organization's permissions load. The remounted list would then seed
   * from the previous organization's ?q= / ?scope= and restore them. So a mount
   * whose path last ran under a different organization — or under this one with
   * an organization switch since (A → B → A while the page was withdrawn, see
   * noteOrganizationSwitch) — ignores the URL seed and
   * strips those params — the same outcome as an in-place switch. A first visit
   * (a pasted link) still applies its query, as rule 5 requires.
   */
  const staleUrlRef = useRef<boolean | null>(null);
  if (staleUrlRef.current === null) {
    const previous = lastResetKeyByPath.get(routePath);
    staleUrlRef.current =
      urlEnabled &&
      resetKey != null &&
      previous !== undefined &&
      (previous.key !== resetKey || previous.switchSeq !== organizationSwitchSeq);
  }

  const seedRef = useRef<UrlSeed<S> | null>(null);
  if (seedRef.current === null) {
    seedRef.current = readUrlSeed(
      staleUrlRef.current ? null : searchParams,
      filterConfig,
      sortColumns,
      initialSort,
    );
  }
  const seed = seedRef.current;

  useEffect(() => {
    if (urlEnabled && resetKey != null) {
      lastResetKeyByPath.set(routePath, { key: resetKey, switchSeq: organizationSwitchSeq });
    }
  }, [urlEnabled, routePath, resetKey]);

  // ---- Query state ---------------------------------------------------
  const [searchInput, setSearchInputState] = useState(seed.search);
  const [search, setSearch] = useState(seed.search);
  const [sort, setSort] = useState<ListSort<S> | undefined>(seed.sort);
  const [filters, setFilters] = useState<FilterValues>(seed.filters);
  const [resetToken, setResetToken] = useState(0);
  const filterDefaults = useMemo(() => defaultsOf(filterConfig), [filterConfig]);
  const filterDefaultsRef = useRef(filterDefaults);
  filterDefaultsRef.current = filterDefaults;
  const initialSortRef = useRef(initialSort);

  // Organization switch: back to defaults. Runs before the fetch effect.
  const previousResetKey = useRef(resetKey);
  const [scopeToken, setScopeToken] = useState(0);
  useEffect(() => {
    if (previousResetKey.current === resetKey) return;
    const leftKey = previousResetKey.current;
    previousResetKey.current = resetKey;
    setSearchInputState('');
    setSearch('');
    setSort(initialSortRef.current);
    setFilters(filterDefaultsRef.current);
    setScopeToken((token) => token + 1);
    // Strip this list's params NOW, in the same commit that saw the switch.
    // Waiting for the URL-mirror effect's next run is not enough: PermissionGate
    // usually unmounts the page one render later, and the remount would seed
    // from the previous organization's ?q= / ?scope= (seen in E2E, sensors-table
    // "resets the sensor list…"). Only between two organizations — a first
    // organization arriving under a pasted link keeps the link's query.
    if (urlEnabled && setSearchParams && leftKey != null && resetKey != null) {
      const owned = ['q', 'sort', 'dir', ...Object.keys(filterDefaultsRef.current)];
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const key of owned) next.delete(key);
          return next;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  // Debounced, trimmed search.
  useEffect(() => {
    const next = searchInput.trim();
    if (next === search) return undefined;
    const handle = window.setTimeout(() => setSearch(next), debounceMs);
    return () => window.clearTimeout(handle);
  }, [searchInput, search, debounceMs]);

  // Mirror the applied query into the URL (replace; defaults omitted).
  const firstUrlWrite = useRef(true);
  useEffect(() => {
    if (!urlEnabled || !setSearchParams) return;
    if (firstUrlWrite.current) {
      firstUrlWrite.current = false;
      // A stale query from the organization just left is written away now;
      // otherwise the URL already says what the seed read.
      if (!staleUrlRef.current) return;
    }
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (search) next.set('q', search);
        else next.delete('q');
        const initial = initialSortRef.current;
        if (sort && (!initial || sort.column !== initial.column || sort.direction !== initial.direction)) {
          next.set('sort', sort.column);
          next.set('dir', sort.direction);
        } else {
          next.delete('sort');
          next.delete('dir');
        }
        for (const [name, value] of Object.entries(filters)) {
          if (value === filterDefaultsRef.current[name] || value === '') next.delete(name);
          else next.set(name, String(value));
        }
        return next;
      },
      { replace: true },
    );
  }, [urlEnabled, setSearchParams, search, sort, filters]);

  // ---- Data state ----------------------------------------------------
  const [state, setState] = useState<ServerState<T>>(() => ({
    scope: resetKey,
    items: [],
    nextCursor: null,
    totalCount: null,
    status: 'initial',
    error: null,
    loaded: false,
    marks: new Map(),
    generation: 0,
    lastRemoval: null,
  }));
  const stateRef = useRef(state);
  stateRef.current = state;

  // Memory source: the whole array, plus how deep the window reaches.
  const [memoryRows, setMemoryRows] = useState<readonly T[] | null>(null);
  const memoryRowsRef = useRef(memoryRows);
  memoryRowsRef.current = memoryRows;
  const [windowSize, setWindowSize] = useState(LIST_BATCH_SIZE);
  const [pinnedKeys, setPinnedKeys] = useState<string[]>([]);
  const memoryOrderRef = useRef<string>('');

  const generationRef = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const autoLoadsRef = useRef(0);
  const stallRef = useRef(0);
  const servedKeysRef = useRef<Set<string>>(new Set());
  const removalTokenRef = useRef(0);

  // A memory list re-reads its source only on an organization switch or
  // reset(); search, sort and filters re-query the rows it already holds.
  const queryKey = isMemory
    ? JSON.stringify([resetKey, resetToken, scopeToken])
    : JSON.stringify([resetKey, search, sort ?? null, filters, resetToken, scopeToken]);
  const sourceKey = isMemory ? (options as MemoryListOptions<T, S>).sourceKey : undefined;

  const logError = useCallback((err: unknown) => {
    console.error(errorContext ?? 'List load failed', err);
  }, [errorContext]);

  const runRequest = useCallback(
    <R,>(task: () => Promise<R>): Promise<R> => (queue ? queue.run(task) : task()),
    [queue],
  );

  // ---- First batch (server) / load (memory) --------------------------
  useEffect(() => {
    if (!enabled) return undefined;
    const generation = ++generationRef.current;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    autoLoadsRef.current = 0;
    stallRef.current = 0;

    const scopeChanged = stateRef.current.scope !== resetKey;
    setState((previous) => ({
      ...previous,
      scope: resetKey,
      items: scopeChanged ? [] : previous.items,
      totalCount: scopeChanged ? null : previous.totalCount,
      nextCursor: scopeChanged ? null : previous.nextCursor,
      marks: scopeChanged ? new Map() : previous.marks,
      loaded: scopeChanged ? false : previous.loaded,
      // A failed load keeps its error until a request for the current query
      // succeeds (Retry shows pending); a failed batch's error does not
      // outlive the query it belonged to.
      error: previous.status === 'error' && !scopeChanged ? previous.error : null,
      status:
        scopeChanged || (optionsRef.current.source !== 'memory' && !previous.loaded) ||
        (optionsRef.current.source === 'memory' && memoryRowsRef.current === null)
          ? 'initial'
          : 'refreshing',
    }));

    const current = optionsRef.current;
    if (current.source === 'memory') {
      if (scopeChanged) setMemoryRows(null);
      Promise.resolve()
        .then(() => current.load({ signal: controller.signal }))
        .then((rows) => {
          if (generation !== generationRef.current) return;
          setMemoryRows(rows);
          setPinnedKeys([]);
          setWindowSize(LIST_BATCH_SIZE);
          setState((previous) => ({
            ...previous,
            status: 'ready',
            error: null,
            marks: new Map(),
            generation: previous.generation + 1,
            lastRemoval: null,
          }));
        })
        .catch((err: unknown) => {
          if (generation !== generationRef.current || isAbort(err)) return;
          logError(err);
          setState((previous) => ({ ...previous, status: 'error', error: err }));
        });
      return () => controller.abort();
    }

    runRequest(() =>
      current.fetcher({
        cursor: null,
        limit: LIST_BATCH_SIZE,
        search,
        sort,
        filters,
        signal: controller.signal,
      }),
    )
      .then((batch) => {
        if (generation !== generationRef.current) return;
        const keys = new Set<string>();
        const items: T[] = [];
        for (const row of batch.items) {
          const key = rowKeyRef.current(row);
          if (keys.has(key)) continue;
          keys.add(key);
          items.push(row);
        }
        servedKeysRef.current = keys;
        const hasMore = batch.nextCursor !== null;
        setState((previous) => ({
          ...previous,
          items,
          nextCursor: batch.nextCursor,
          totalCount: typeof batch.totalCount === 'number' ? batch.totalCount : null,
          status: hasMore && items.length >= capAt ? 'capped' : 'ready',
          error: null,
          loaded: true,
          marks: new Map(),
          generation: previous.generation + 1,
          lastRemoval: null,
        }));
      })
      .catch((err: unknown) => {
        if (generation !== generationRef.current || isAbort(err)) return;
        logError(err);
        setState((previous) => ({ ...previous, status: 'error', error: err }));
      });
    return () => controller.abort();
    // queryKey captures resetKey/search/sort/filters/resetToken.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, queryKey]);

  // Memory source changed identity: re-read, keep the revealed depth.
  const firstSourceKey = useRef(true);
  useEffect(() => {
    if (!isMemory) return undefined;
    if (firstSourceKey.current) {
      firstSourceKey.current = false;
      return undefined;
    }
    if (!enabled) return undefined;
    const current = optionsRef.current;
    if (current.source !== 'memory') return undefined;
    const controller = new AbortController();
    let cancelled = false;
    Promise.resolve()
      .then(() => current.load({ signal: controller.signal }))
      .then((rows) => {
        if (cancelled) return;
        setMemoryRows(rows);
      })
      .catch((err: unknown) => {
        if (cancelled || isAbort(err)) return;
        logError(err);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey]);

  // ---- Append --------------------------------------------------------
  const append = useCallback(() => {
    const current = optionsRef.current;
    if (current.source === 'memory') return;
    const snapshot = stateRef.current;
    const cursor = snapshot.nextCursor;
    if (cursor === null) return;
    const generation = generationRef.current;
    const controller = new AbortController();
    controllerRef.current = controller;
    setState((previous) => ({ ...previous, status: 'loadingMore' }));
    runRequest(() =>
      current.fetcher({
        cursor,
        limit: LIST_BATCH_SIZE,
        search,
        sort,
        filters,
        signal: controller.signal,
      }),
    )
      .then((batch) => {
        if (generation !== generationRef.current) return;
        setState((previous) => {
          const existing = new Set(previous.items.map((row) => rowKeyRef.current(row)));
          const added: T[] = [];
          for (const row of batch.items) {
            const key = rowKeyRef.current(row);
            servedKeysRef.current.add(key);
            if (existing.has(key)) continue;
            existing.add(key);
            added.push(row);
          }
          stallRef.current = added.length === 0 && batch.nextCursor !== null ? stallRef.current + 1 : 0;
          const items = added.length ? [...previous.items, ...added] : previous.items;
          const hasMore = batch.nextCursor !== null;
          let status: ListStatus = 'ready';
          if (hasMore && stallRef.current >= 2) status = 'stalled';
          else if (hasMore && items.length >= capAt) status = 'capped';
          return {
            ...previous,
            items,
            nextCursor: batch.nextCursor,
            totalCount:
              previous.totalCount === null && typeof batch.totalCount === 'number'
                ? batch.totalCount
                : previous.totalCount,
            status,
            error: null,
          };
        });
      })
      .catch((err: unknown) => {
        if (generation !== generationRef.current || isAbort(err)) return;
        logError(err);
        setState((previous) => ({ ...previous, status: 'appendError', error: err }));
      });
  }, [runRequest, search, sort, filters, capAt, logError]);

  // ---- Memory window -------------------------------------------------
  /** Rows prepended since the last read of the source (creates), by key. */
  const [memoryPinned, setMemoryPinned] = useState<Map<string, T>>(() => new Map());
  const memoryView = useMemo(() => {
    if (!isMemory || memoryRows === null) return null;
    const memoryOptions = options as MemoryListOptions<T, S>;
    // A created row takes part in search, sort and filters like a loaded one, so
    // searching for the record just created finds it (ruling 20). Until then it
    // is pinned first; a query change unpins it into its sorted place.
    const loadedKeys = memoryPinned.size ? new Set(memoryRows.map((row) => rowKeyRef.current(row))) : null;
    const source = loadedKeys
      ? [...Array.from(memoryPinned.values()).filter((row) => !loadedKeys.has(rowKeyRef.current(row))), ...memoryRows]
      : memoryRows;
    const filterFn = memoryOptions.memory.filter;
    const filtered = filterFn ? source.filter((row) => filterFn(row, filters)) : source;
    return queryInMemoryWindow(filtered, { search, sort }, memoryOptions.memory);
    // `options.memory` is config; its identity is not a reason to re-sort.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMemory, memoryRows, memoryPinned, search, sort, filters]);

  // Reset the depth when the QUERY changes (not when the source does).
  useEffect(() => {
    const signature = JSON.stringify([search, sort ?? null, filters]);
    if (memoryOrderRef.current && memoryOrderRef.current !== signature) {
      setWindowSize(LIST_BATCH_SIZE);
      setPinnedKeys([]);
      setState((previous) => ({ ...previous, marks: new Map(), generation: previous.generation + 1 }));
    }
    memoryOrderRef.current = signature;
  }, [search, sort, filters]);

  // Memory patches live on the source rows; ordering stays as queried.
  const [memoryPatches, setMemoryPatches] = useState<Map<string, T>>(() => new Map());
  const [memoryRemoved, setMemoryRemoved] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    // A fresh read of the source is authoritative again. A row removed here
    // stays removed: it is dropped from the pinned creates too, which would
    // otherwise bring a record deleted after it was created straight back once
    // the removal set is cleared.
    if (memoryRemoved.size > 0) {
      const removed = memoryRemoved;
      setMemoryPinned((previous) => {
        const next = new Map(previous);
        removed.forEach((key) => next.delete(key));
        return next.size === previous.size ? previous : next;
      });
      setPinnedKeys((previous) => previous.filter((key) => !removed.has(key)));
    }
    setMemoryPatches(new Map());
    setMemoryRemoved(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoryRows]);

  // ---- Derived -------------------------------------------------------
  const scopeMatches = state.scope === resetKey;
  let items: T[];
  let totalCount: number | null;
  let hasMore: boolean;
  let status: ListStatus = !enabled || !scopeMatches ? 'initial' : state.status;

  if (isMemory) {
    const view = memoryView ?? [];
    const pinned = pinnedKeys
      .map((key) => memoryPatches.get(key) ?? view.find((row) => rowKey(row) === key) ?? memoryPinned.get(key))
      .filter((row): row is T => row !== undefined && !memoryRemoved.has(rowKey(row)));
    const pinnedSet = new Set(pinnedKeys);
    const rest = view.filter((row) => {
      const key = rowKey(row);
      return !pinnedSet.has(key) && !memoryRemoved.has(key);
    });
    const revealed = rest.slice(0, windowSize).map((row) => memoryPatches.get(rowKey(row)) ?? row);
    items = memoryRows === null || !scopeMatches ? [] : [...pinned, ...revealed];
    totalCount = memoryRows === null || !scopeMatches ? null : pinned.length + rest.length;
    hasMore = memoryRows !== null && windowSize < rest.length;
    if (status === 'ready' && hasMore && items.length >= capAt) status = 'capped';
  } else {
    items = scopeMatches ? state.items : [];
    totalCount = scopeMatches ? state.totalCount : null;
    hasMore = scopeMatches && state.nextCursor !== null;
  }

  // ---- Actions -------------------------------------------------------
  const loadMore = useCallback(
    (loadOptions?: { auto?: boolean }) => {
      const current = stateRef.current;
      if (!enabled || current.status !== 'ready') return;
      if (loadOptions?.auto) {
        if (autoLoadsRef.current >= LIST_AUTO_FILL_MAX) return;
        autoLoadsRef.current += 1;
      } else {
        autoLoadsRef.current = 0;
      }
      if (optionsRef.current.source === 'memory') {
        setWindowSize((size) => size + LIST_BATCH_SIZE);
        return;
      }
      if (current.nextCursor === null) return;
      append();
    },
    [enabled, append],
  );

  const retryBatch = useCallback(() => {
    if (stateRef.current.status !== 'appendError') return;
    autoLoadsRef.current = 0;
    append();
  }, [append]);

  const reset = useCallback(() => setResetToken((token) => token + 1), []);

  const patchItem = useCallback(
    (key: string, fn: (row: T) => T, patchOptions?: { mark?: RowMark }) => {
      const mark = patchOptions?.mark;
      const setMark = (marks: Map<string, RowMark>) => {
        if (!mark) return marks;
        const next = new Map(marks);
        // A row created in this session stays 'new' after an edit.
        if (next.get(key) !== 'new') next.set(key, mark);
        return next;
      };
      if (optionsRef.current.source === 'memory') {
        const view = [
          ...memoryPinned.values(),
          ...(memoryRows ?? []),
        ];
        const base = memoryPatches.get(key) ?? view.find((row) => rowKeyRef.current(row) === key);
        if (!base) return;
        const patched = fn(base);
        setMemoryPatches((previous) => new Map(previous).set(key, patched));
        setState((previous) => ({ ...previous, marks: setMark(previous.marks) }));
        return;
      }
      setState((previous) => {
        const index = previous.items.findIndex((row) => rowKeyRef.current(row) === key);
        if (index < 0) return previous;
        const items = previous.items.slice();
        items[index] = fn(items[index]);
        return { ...previous, items, marks: setMark(previous.marks) };
      });
    },
    [memoryPatches, memoryPinned, memoryRows],
  );

  const removeItem = useCallback(
    (key: string) => {
      const token = ++removalTokenRef.current;
      const visible = items;
      const index = visible.findIndex((row) => rowKeyRef.current(row) === key);
      if (index < 0) return;
      const neighbour = visible[index + 1] ?? visible[index - 1];
      const lastRemoval: ListRemoval = {
        index,
        nextKey: neighbour ? rowKeyRef.current(neighbour) : null,
        token,
      };
      if (optionsRef.current.source === 'memory') {
        setMemoryRemoved((previous) => new Set(previous).add(key));
        setState((previous) => {
          const marks = new Map(previous.marks);
          marks.delete(key);
          return { ...previous, marks, lastRemoval };
        });
        return;
      }
      const adjust = (optionsRef.current as ServerListOptions<T, S>).cursorAfterRemove;
      const served = servedKeysRef.current.has(key);
      servedKeysRef.current.delete(key);
      setState((previous) => {
        const marks = new Map(previous.marks);
        marks.delete(key);
        return {
          ...previous,
          items: previous.items.filter((row) => rowKeyRef.current(row) !== key),
          totalCount: previous.totalCount === null ? null : Math.max(0, previous.totalCount - 1),
          nextCursor:
            served && adjust && previous.nextCursor !== null ? adjust(previous.nextCursor) : previous.nextCursor,
          marks,
          lastRemoval,
        };
      });
    },
    [items],
  );

  const prependItem = useCallback((row: T, prependOptions?: { mark?: RowMark }) => {
    const key = rowKeyRef.current(row);
    const mark = prependOptions?.mark;
    if (optionsRef.current.source === 'memory') {
      setMemoryPinned((previous) => new Map(previous).set(key, row));
      setMemoryPatches((previous) => {
        if (!previous.has(key)) return previous;
        const next = new Map(previous);
        next.delete(key);
        return next;
      });
      setMemoryRemoved((previous) => {
        if (!previous.has(key)) return previous;
        const next = new Set(previous);
        next.delete(key);
        return next;
      });
      setPinnedKeys((previous) => [key, ...previous.filter((k) => k !== key)]);
      setState((previous) => {
        const marks = new Map(previous.marks);
        if (mark) marks.set(key, mark);
        return { ...previous, marks };
      });
      return;
    }
    setState((previous) => {
      const exists = previous.items.some((existing) => rowKeyRef.current(existing) === key);
      const marks = new Map(previous.marks);
      if (mark) marks.set(key, mark);
      return {
        ...previous,
        items: [row, ...previous.items.filter((existing) => rowKeyRef.current(existing) !== key)],
        totalCount: exists || previous.totalCount === null ? previous.totalCount : previous.totalCount + 1,
        marks,
      };
    });
  }, []);

  const setSearchInput = useCallback((value: string) => setSearchInputState(value), []);

  const clearSearch = useCallback(() => {
    setSearchInputState('');
    setSearch('');
  }, []);

  const onSortChange = useCallback((next: SortState) => {
    const allowed = optionsRef.current.sortColumns;
    if (allowed && !(allowed as readonly string[]).includes(next.column)) return;
    setSort({ column: next.column as S, direction: next.direction });
  }, []);

  const setFilter = useCallback((name: string, value: FilterValue) => {
    if (!acceptsFilter(optionsRef.current.filters, name, value)) return;
    setFilters((previous) => (previous[name] === value ? previous : { ...previous, [name]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters((previous) =>
      sameFilters(previous, filterDefaultsRef.current) ? previous : filterDefaultsRef.current,
    );
    setSearchInputState('');
    setSearch('');
  }, []);

  const hasActiveFilters = !sameFilters(filters, filterDefaults);
  const totalIsApproximate = totalCount !== null && items.length > totalCount;

  return {
    items,
    totalCount,
    totalIsApproximate,
    hasMore,
    status,
    error: scopeMatches ? state.error : null,
    loadMore,
    retryBatch,
    reset,
    patchItem,
    removeItem,
    prependItem,
    searchInput,
    setSearchInput,
    clearSearch,
    sort,
    onSortChange,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
    search,
    marks: state.marks,
    generation: state.generation,
    lastRemoval: state.lastRemoval,
  };
}
